import { get as val } from 'object-path'
import * as immu from 'object-path-immutable'

// Names export
export function provider( xeon ) {

  // Exposure changes
  const aucs = Object.values( xeon.rules ).reduce( ( acc, rule ) => {
    if ( rule.group == "kinetic" ) {
      if ( rule.predict && rule.predict.auc_ratio ) {
        acc[ rule.drug ] = rule.predict.auc_ratio
      }
    }
    return acc
  }, {} );

  const rules = Object.values( xeon.rules ).filter( rule => {
    return rule.group == "adverse" && rule.param && rule.param.some( l => true )
  } )

  return {

    // Score between 0 -100
    score: function ( scale = 0.5 ) {
      const notNegativ = Math.max( 0, this.scoring().value )
      return Math.min( 100, parseInt( notNegativ * scale ) )
    },

    // ----------------------------------------------
    // Give adverse a score
    // e.g. name:"Schläfrigkeit", value: 3.98, score: 8.2
    // ----------------------------------------------
    scoring: function () {
      const sum = this.abstract( "de" ).reduce( ( acc, cur, idx, arr ) => {
        // Prevents NaN on Score
        if ( !cur.score ) return acc
        // return acc + ( ( ( cur.value - 1 ) * 5 ) / arr.length )
        return acc + cur.score
      }, 0 )
      return { value: sum }
    },

    // List of top adverse events
    // Scale 0-100 to 1-10
    // Only top three events for scoring
    // {
    //   text, group:"neuro", drugs: { aripi: { id, freq } }, sum
    // }
    abstract: function ( lang ) {
      // Check all adverse events and filter for combinations, which
      // will influence the scoring. Only the incremental risk is
      // relevant for scoring.
      const items = this.hitlist( lang ).filter(item => {
        return Object.keys( item.drugs ).length > 1
      }).slice( 0, 3 )
      const rows = []
      // Keep stable 3 rows not to change height by clicking
      for ( var i = 0; i < 3; i++ ) {
        if ( items[ i ] ) {
          // console.log( items[ i ] )
          // Find the maximum frequency for the drugs
          const max = Object.values( items[ i ].drugs )
            .filter( drug => drug.freq > 0 )
            .map( drug => drug.freq )
            .reduce( ( acc, cur ) => {
              return Math.max( acc, cur )
            }, 0 )
          //
          rows.push( {
            name: items[ i ].text,
            // Make the value fit into the grey matrix
            value: items[ i ].sum ? items[ i ].sum / 10 + 1 : null,
            // For scoring
            score: items[ i ].sum ? items[ i ].sum - max : null,
          } )
          // console.log( rows )
        } else {
          rows.push( {
            name: "-/-",
            value: null
          } )
        }
      }
      return rows
    },

    // ------------------------
    // List of adverse events
    // ------------------------
    /*
    @param lang
    @return
    [
      {
        "group": "cardi",
        "drug": "diltiazem",
        "keya": "heart_failure",
        "text": "Herzinsuffizienz",
        "freq": 1.9,
        "aucr": 1.45
      },
      {
        "group": "cardi",
        "drug": "diltiazem",
        "keya": "myocardial_infarction",
        "text": "Herzinfarkt",
        "freq": 0.01,
        "aucr": 1.45
      },
      ...
    ]
    */
    prepare: function ( lang = "de" ) {
      const adverse = []

      for ( const rule of rules ) {

        // Find drug
        const drug = rule[ "drug" ]
        // Find translation
        const dict = rule[ "text" ][ lang ]

        // Iterate lines
        for ( const line of rule[ "param" ] ) {
          // Only mild adverse and not extra
          if ( !line.includes( "extra" ) ) {
            // Destruct
            const [ group, key, ...rest ] = line.split( " " )
            // Default frequency
            var def = 0.01
            if ( line.includes( "grad:serious" ) ) {
              def = 0.01
            }
            if ( line.includes( "grad:mild" ) ) {
              def = 1
            }

            // Get frequency or set default to 1
            const freq = rest.reduce( ( acc, cur ) => {
              if ( cur.includes( "freq" ) ) {
                return parseFloat( cur.replace( "freq:", "" ) )
              } else {
                return acc
              }
            }, def )
            // Add to list
            adverse.push( {
              group: group,
              drug: drug,
              grad: line.includes( "grad:serious" ) ? "serious" : "mild",
              keya: key,
              text: dict[ key ],
              freq: freq,
              aucr: aucs[ drug ] || null
            } )
          }

        }
      }

      return adverse
    },

    // ----------------------------------------
    // Create hitlist for text and table
    // ----------------------------------------
    /*
    [
      {
        "text": "Kopfschmerzen",
        "group": "neuro",
        "drugs": {
          "diltiazem": {
            "id": "diltiazem",
            "freq": 4.6,
            "aucr": 1,
            "grad": "mild"
          },
          "triazolam": {
            "id": "triazolam",
            "freq": 9.7,
            "aucr": 3.46,
            "grad": "mild"
          }
        },
        "sum": 13.9
      }
      ,...
    ]
    */
    hitlist: function ( lang ) {

      const adverse = this.prepare( lang )
      const liste = {}

      // Group single events
      for ( const ade of adverse ) {

        liste[ ade.keya ] = liste[ ade.keya ] || {
          text: ade.text,
          group: ade.group,
          drugs: {}
        }

        liste[ ade.keya ][ "drugs" ][ ade.drug ] = {
          id: ade.drug,
          freq: ade.freq,
          aucr: ade.aucr,
          grad: ade.grad,
        }

      }

      for ( const key in liste ) {
        liste[ key ][ "sum" ] = Object.values( liste[ key ][ "drugs" ] ).filter( drug => {
          return drug.freq > 0.1
        } ).reduce( calc, 0 )
      }

      return Object.values( liste ).sort( ( a, b ) => {
        return b.sum - a.sum
      } )
    },
    table: function ( lang ) {
      return this.hitlist( lang ).slice( 0, 10 )
    },
    /* Transform hitlist for text
      [
        {
          name: "neuro",
          items: [
            text: "Kopfschmerz",
            freq: 14.1.
            list: [
              { id:"sivastatim", text: "Kopfschmerz", freq: 13.9, grad: "mild" }
            ]
          ]
        },...
      ]
    */
    text: function ( lang ) {
      return Object.values( this.hitlist( lang ).slice( 10 ).reduce( ( acc, cur ) => {
        acc[ cur.group ] = acc[ cur.group ] || {
          name: cur.group,
          items: []
        }
        acc[ cur.group ][ "items" ].push( {
          text: cur.text,
          freq: cur.sum,
          list: Object.values( cur.drugs )
        } )
        return acc
      }, {} ) )
    }

  }

}

// -----------------------------------------------
// Calculate combined frequency in reduce
// -----------------------------------------------
function calc( acc, cur, idx, arr ) {
  // Normalize
  cur = cur.freq / 100
  // Init
  acc = acc + ( 1 - acc ) * cur
  // More calculation
  if ( idx < arr.length - 1 ) return acc
  // Final
  return +( 100 * acc ).toFixed( 1 )
}
